import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectSelection.module.css';
import { Project, User } from '../../types';
import { Header } from '../../components/layout/Header/Header';
import { dossierAPI } from '../../services/api';

const FUNDAMENT_FASEN = [
  { key: 'projectverkenning', label: 'Projectverkenning' },
  { key: 'omgevingsanalyse', label: 'Omgevingsanalyse' },
  { key: 'personas', label: "Persona's" },
  { key: 'strategie', label: 'Communicatiestrategie' },
  { key: 'kernboodschap', label: 'Kernboodschap' },
];

type FaseStatus = 'gereed' | 'actief' | 'open';
interface FaseInfo { statuses: FaseStatus[]; gevalideerd: number; currentNr: number; lastUpdate: string | null; }

interface ProjectSelectionProps {
  projects: Project[];
  user: User;
  isAdmin: boolean;
  onProjectSelect: (project: Project) => void;
  onLogout: () => void;
}

const STATUS_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  active: { label: 'Actief', bg: '#eafaf0', color: '#1f7a44' },
  planning: { label: 'In afwachting', bg: '#eef4ff', color: '#2563eb' },
  paused: { label: 'Gepauzeerd', bg: '#fff6e6', color: '#b45309' },
  completed: { label: 'Afgerond', bg: '#eafaf0', color: '#1f7a44' },
  cancelled: { label: 'Geannuleerd', bg: '#fdecec', color: '#b91c1c' },
};

function statusBadge(project: Project) {
  const s = (project.status as string) || 'planning';
  return STATUS_BADGE[s] || { label: project.statusLabel || s, bg: '#f1f3f6', color: '#6b7280' };
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function projectYear(project: Project): string {
  return project.period || (project.startDate ? String(new Date(project.startDate).getFullYear()) : '');
}

const Icon = {
  search: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  chevron: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
  list: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  grid: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
  pin: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  cal: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  dots: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>,
  plus: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  gear: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>,
};

const PAGE_SIZE = 8;

export const ProjectSelection: React.FC<ProjectSelectionProps> = ({ projects, user, isAdmin, onLogout }) => {
  const navigate = useNavigate();
  const [faseInfo, setFaseInfo] = useState<Record<string, FaseInfo>>({});
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'recent' | 'naam'>('recent');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [page, setPage] = useState(1);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const projectIds = projects.map((p) => p.id).join(',');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(projects.map(async (p) => {
        try {
          const res = await dossierAPI.getByProject(p.id);
          const s = res?.dossier?.sections || {};
          const gevalideerd = FUNDAMENT_FASEN.filter((f) => s[f.key]?.status === 'locked').length;
          const statuses: FaseStatus[] = FUNDAMENT_FASEN.map((_, i) =>
            i < gevalideerd ? 'gereed' : (i === gevalideerd ? 'actief' : 'open'));
          const updates = FUNDAMENT_FASEN.map((f) => s[f.key]?.updatedAt).filter(Boolean) as string[];
          const lastUpdate = updates.length
            ? updates.sort().slice(-1)[0]
            : (res?.dossier?.updatedAt || p.startDate || null);
          const currentNr = gevalideerd < FUNDAMENT_FASEN.length ? gevalideerd + 1 : FUNDAMENT_FASEN.length;
          return [p.id, { statuses, gevalideerd, currentNr, lastUpdate }] as const;
        } catch {
          return [p.id, {
            statuses: FUNDAMENT_FASEN.map((_, i) => (i === 0 ? 'actief' : 'open') as FaseStatus),
            gevalideerd: 0, currentNr: 1, lastUpdate: p.startDate || null,
          }] as const;
        }
      }));
      if (!cancelled) setFaseInfo(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIds]);

  const openProject = (project: Project) => navigate(`/project/${project.id}/dashboard`);
  const editProject = (project: Project) => navigate(`/admin/projects/${project.id}/edit`);

  const filtered = useMemo(() => {
    let list = projects.slice();
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => `${p.fullName || p.name} ${p.location || ''}`.toLowerCase().includes(q));
    if (statusFilter !== 'all') list = list.filter((p) => (p.status as string) === statusFilter);
    if (sort === 'naam') {
      list.sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name));
    } else {
      list.sort((a, b) => {
        const ta = new Date(faseInfo[a.id]?.lastUpdate || a.startDate || 0).getTime();
        const tb = new Date(faseInfo[b.id]?.lastUpdate || b.startDate || 0).getTime();
        return tb - ta;
      });
    }
    return list;
  }, [projects, query, statusFilter, sort, faseInfo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const thumb = (project: Project): string =>
    project.headerImage ? project.headerImage : `/assets/images/${project.image}`;

  // ---- Fase-stepper (horizontaal) ----
  const Stepper = ({ info, compact }: { info?: FaseInfo; compact?: boolean }) => {
    const statuses = info?.statuses || FUNDAMENT_FASEN.map(() => 'open' as FaseStatus);
    return (
      <div style={{ position: 'relative', marginTop: compact ? 12 : 24 }}>
        <div style={{ position: 'absolute', top: 17, left: '10%', right: '10%', height: 2, background: '#e6e8ee' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          {FUNDAMENT_FASEN.map((f, i) => {
            const st = statuses[i];
            const bg = st === 'gereed' ? '#1f9d55' : st === 'actief' ? '#1e3a2f' : '#eef0f4';
            const col = st === 'open' ? '#9aa1b0' : '#fff';
            return (
              <div key={f.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0, padding: '0 4px' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: bg, color: col, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, boxShadow: st === 'actief' ? '0 0 0 4px #e8eee6' : (st === 'gereed' ? '0 0 0 4px #e3f5ea' : 'none') }}>
                  {st === 'gereed' ? Icon.check : i + 1}
                </div>
                {!compact && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginTop: 8, textAlign: 'center', lineHeight: 1.25 }}>{f.label}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const Menu = ({ project }: { project: Project }) => (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === project.id ? null : project.id); }}
        title="Acties"
        style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: '#9aa1b0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {Icon.dots}
      </button>
      {menuOpen === project.id && (
        <>
          <div onClick={(e) => { e.stopPropagation(); setMenuOpen(null); }} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div style={{ position: 'absolute', right: 0, top: 36, zIndex: 21, background: '#fff', border: '1px solid #ececf1', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden' }}>
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(null); openProject(project); }} style={menuItemStyle}>Openen</button>
            {isAdmin && <button onClick={(e) => { e.stopPropagation(); setMenuOpen(null); editProject(project); }} style={menuItemStyle}>Bewerken</button>}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={styles.projectSelection}>
      <Header user={user} showProjectSelector={false} onLogout={onLogout} />

      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', padding: '32px 32px 48px', boxSizing: 'border-box' }}>
        {/* Kop */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.5px' }}>Mijn projecten</h1>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => navigate('/admin/projects/new')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer' }}>
                {Icon.plus} Nieuw project
              </button>
            </div>
          )}
        </div>

        {projects.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #ececf1', borderRadius: 16, padding: '56px 24px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ marginBottom: 12, color: '#c3c9d4' }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#1a1a2e' }}>Geen projecten beschikbaar</h2>
            <p style={{ margin: '0 0 16px' }}>{isAdmin ? 'Maak een nieuw project aan of wijs jezelf toe via Admin beheer.' : 'Je bent nog niet toegewezen aan een project. Neem contact op met je beheerder.'}</p>
            {isAdmin && (
              <button onClick={() => navigate('/admin/projects/new')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer' }}>
                {Icon.plus} Nieuw project aanmaken
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220, maxWidth: 380, background: '#fff', border: '1px solid #e2e5ea', borderRadius: 10, padding: '0 12px' }}>
                <span style={{ color: '#9aa1b0' }}>{Icon.search}</span>
                <input value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Zoek projecten…"
                  style={{ border: 'none', outline: 'none', padding: '10px 0', fontSize: 14, width: '100%', background: 'transparent', color: '#1a1a2e' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  style={{ appearance: 'none', WebkitAppearance: 'none', padding: '10px 34px 10px 36px', borderRadius: 10, border: '1px solid #e2e5ea', background: '#fff', fontSize: 14, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                  <option value="all">Filters: alle</option>
                  <option value="active">Actief</option>
                  <option value="planning">In afwachting</option>
                  <option value="paused">Gepauzeerd</option>
                  <option value="completed">Afgerond</option>
                </select>
                <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', pointerEvents: 'none' }}>{Icon.filter}</span>
                <span style={{ position: 'absolute', right: 10, top: 12, color: '#9aa1b0', pointerEvents: 'none' }}>{Icon.chevron}</span>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ position: 'relative' }}>
                  <select value={sort} onChange={(e) => setSort(e.target.value as any)}
                    style={{ appearance: 'none', WebkitAppearance: 'none', padding: '10px 34px 10px 14px', borderRadius: 10, border: '1px solid #e2e5ea', background: '#fff', fontSize: 14, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                    <option value="recent">Sorteren: meest recent</option>
                    <option value="naam">Sorteren: naam (A–Z)</option>
                  </select>
                  <span style={{ position: 'absolute', right: 10, top: 12, color: '#9aa1b0', pointerEvents: 'none' }}>{Icon.chevron}</span>
                </div>
                <div style={{ display: 'flex', border: '1px solid #e2e5ea', borderRadius: 10, overflow: 'hidden' }}>
                  <button onClick={() => setView('list')} title="Lijst" style={{ padding: '9px 11px', border: 'none', background: view === 'list' ? '#f4f6f1' : '#fff', color: view === 'list' ? '#1e3a2f' : '#9aa1b0', cursor: 'pointer', display: 'flex' }}>{Icon.list}</button>
                  <button onClick={() => setView('grid')} title="Tegels" style={{ padding: '9px 11px', border: 'none', borderLeft: '1px solid #e2e5ea', background: view === 'grid' ? '#f4f6f1' : '#fff', color: view === 'grid' ? '#1e3a2f' : '#9aa1b0', cursor: 'pointer', display: 'flex' }}>{Icon.grid}</button>
                </div>
              </div>
            </div>

            {/* Lijstweergave */}
            {view === 'list' ? (
              <div style={{ display: 'grid', gap: 14 }}>
                {paged.map((project) => {
                  const info = faseInfo[project.id];
                  const badge = statusBadge(project);
                  return (
                    <div key={project.id} onClick={() => openProject(project)}
                      style={{ display: 'flex', gap: 24, alignItems: 'flex-start', background: '#fff', border: '1px solid #ececf1', borderRadius: 16, padding: 22, cursor: 'pointer' }}>
                      <div style={{ position: 'relative', width: 240, height: 168, flexShrink: 0, borderRadius: 12, backgroundImage: `url(${thumb(project)})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#e6e8ee' }}>
                        {(project as any).company?.logo && (
                          <img src={(project as any).company.logo} alt={(project as any).company?.name || 'Klant'}
                            style={{ position: 'absolute', left: 8, bottom: 8, height: 34, maxWidth: 90, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 3, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }} />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#1a1a2e' }}>{project.fullName || project.name}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, color: '#8a90a0', fontSize: 13.5 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{Icon.pin}{project.location}</span>
                          {projectYear(project) && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{Icon.cal}{projectYear(project)}</span>}
                        </div>
                        <Stepper info={info} />
                      </div>

                      <div style={{ width: 160, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: badge.bg, color: badge.color }}>{badge.label}</span>
                          <Menu project={project} />
                        </div>
                        <div style={{ fontSize: 12, color: '#8a90a0', marginTop: 6 }}>Laatste update</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{formatDate(info?.lastUpdate)}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginTop: 4 }}>Fase {info?.currentNr ?? 1}/{FUNDAMENT_FASEN.length}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Tegelweergave */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
                {paged.map((project) => {
                  const info = faseInfo[project.id];
                  const badge = statusBadge(project);
                  return (
                    <div key={project.id} onClick={() => openProject(project)}
                      style={{ background: '#fff', border: '1px solid #ececf1', borderRadius: 16, overflow: 'hidden', cursor: 'pointer' }}>
                      <div style={{ position: 'relative', height: 150, backgroundImage: `url(${thumb(project)})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#e6e8ee' }}>
                        {(project as any).company?.logo && (
                          <img src={(project as any).company.logo} alt={(project as any).company?.name || 'Klant'}
                            style={{ position: 'absolute', left: 10, bottom: 10, height: 32, maxWidth: 90, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 3, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }} />
                        )}
                        <span style={{ position: 'absolute', right: 10, top: 10, fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: badge.bg, color: badge.color }}>{badge.label}</span>
                      </div>
                      <div style={{ padding: 16 }}>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' }}>{project.fullName || project.name}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, color: '#8a90a0', fontSize: 13 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{Icon.pin}{project.location}</span>
                          {projectYear(project) && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{Icon.cal}{projectYear(project)}</span>}
                        </div>
                        <Stepper info={info} compact />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                          <span>Fase {info?.currentNr ?? 1}/{FUNDAMENT_FASEN.length}</span>
                          <span style={{ color: '#8a90a0', fontWeight: 500 }}>{formatDate(info?.lastUpdate)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Paginatie — alleen bij meerdere pagina's */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 28, color: '#6b7280', fontSize: 14 }}>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} style={pageBtn(false, safePage === 1)}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setPage(n)} style={pageBtn(n === safePage, false)}>{n}</button>
                ))}
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={pageBtn(false, safePage === totalPages)}>›</button>
              </div>
            )}
          </>
        )}

        {/* Afzender: Leene (groene versie), bescheiden onder het overzicht */}
        <div style={{ marginTop: 48, paddingTop: 20, borderTop: '1px solid #e2e5ea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#8a90a0', fontSize: 12.5 }}>
          <span>Een werkomgeving van</span>
          <img src={`${process.env.PUBLIC_URL}/assets/logos/logo_leene.svg`} alt="Leene Communicatie" style={{ height: 20, width: 'auto', display: 'block' }} />
        </div>
      </div>
    </div>
  );
};

const menuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none',
  background: '#fff', color: '#334155', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
};

function pageBtn(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    minWidth: 34, height: 34, padding: '0 8px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    border: active ? '1px solid #1e3a2f' : '1px solid #e2e5ea',
    background: active ? '#f4f6f1' : '#fff', color: active ? '#1e3a2f' : '#334155',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  };
}
